/**
 * tier-config.js (web copy) — single source of the entitlement matrix.
 *
 * Plain JS, NO React / firebase imports. A logical twin of
 * functions/src/tier-config.js (which is CommonJS). The drift-guard test
 * web/src/__tests__/lib/tier-config.test.js imports BOTH copies and asserts they
 * produce identical results on a shared fixture table — so the UI matrix can
 * never silently diverge from server-side enforcement.
 *
 * Hard invariant #1 (voting/RSVP/results/guest-voting NEVER gated) is encoded in
 * DATA: those feature ids are intentionally ABSENT from FEATURE_MIN_TIER, so the
 * `?? "free"` default in canUseFeature() makes them always allowed.
 *
 * Canonical schema + rationale: docs/tier1-commerce-implementation-plan.md §1.4.
 */

// Ordinal comparison: free < lite < pro.
export const TIER_ORDER = { free: 0, lite: 1, pro: 2 };

// Numeric quota caps per tier. null == unlimited.
export const TIER_QUOTAS = {
  free: {
    groups: 1,
    concurrentPolls: 3,
    participantsPerPoll: 15,
    historyRetentionDays: 90,
    discordChannelBindings: 1,
    inviteAllowance: 50,
  },
  lite: {
    groups: 3,
    concurrentPolls: 15,
    participantsPerPoll: 40,
    historyRetentionDays: null,
    discordChannelBindings: 3,
    inviteAllowance: 150,
  },
  pro: {
    groups: null,
    concurrentPolls: null,
    participantsPerPoll: null,
    historyRetentionDays: null,
    discordChannelBindings: null,
    inviteAllowance: 500,
  },
};

// Minimum tier required for each premium feature. Stable feature ids.
// Voting / RSVP / results / guest-voting ids are deliberately absent (invariant #1).
export const FEATURE_MIN_TIER = {
  // Lite+
  RESULT_SORT_STRATEGIES: "lite",
  REQUIRED_ATTENDANCE_FILTER: "lite",
  MULTI_IDENTITY_INVITES: "lite",
  CLONE_SESSION_POLL: "lite",
  AUTO_CREATE_CALENDAR_EVENT: "lite",
  CALENDAR_RICH_DESCRIPTIONS: "lite",
  CALENDAR_ATTENDEES: "lite",
  UNLIMITED_HISTORY: "lite",
  DISCORD_POLL_CARD: "lite",
  DISCORD_REPOST_CARD: "lite",
  AUTOMATED_REMINDERS: "lite",
  // Pro only
  RESCHEDULE_FINALIZED: "pro",
  COPY_VOTES: "pro",
  // NOTE: the old standalone "Auto-Block Conflicts" toggle was removed upstream
  // (folded into the auto-vote engine + per-calendar trustEnabled). Conflict
  // flagging now always evaluates and is NOT tied to any tier. Gating auto-vote
  // autonomy as Pro would be a new product decision — do not re-add a feature id
  // here until that's decided.
  TWO_WAY_CALENDAR_SYNC: "pro",
  OUTBOUND_LIFECYCLE_SYNC: "pro",
  DISCORD_LIVE_POLL_CARD: "pro",
  DISCORD_ROLE_PINGS: "pro",
  DISCORD_SCHEDULED_EVENTS: "pro",
  DISCORD_GRANULAR_ROUTING: "pro",
  VOTER_ANONYMIZATION: "pro",
};

const ACTIVE_STATUSES = new Set(["active", "trialing"]);

function tierRank(tier) {
  const rank = TIER_ORDER[tier];
  return typeof rank === "number" ? rank : TIER_ORDER.free;
}

/**
 * True if `effective` tier meets the minimum required for `featureId`.
 * Unknown / absent feature ids default to "free" → always allowed (invariant #1).
 * Unknown / absent tiers default to "free".
 */
export function canUseFeature(effective, featureId) {
  const minTier = FEATURE_MIN_TIER[featureId] ?? "free";
  return tierRank(effective) >= tierRank(minTier);
}

// Alias for symmetry with the functions copy.
export const isEntitled = canUseFeature;

/** Quota cap for a (tier, key). null == unlimited. Unknown tier → free quota. */
export function quotaFor(effective, key) {
  const quotas = TIER_QUOTAS[effective] || TIER_QUOTAS.free;
  return quotas[key];
}

/**
 * True if creating one more of `key` is allowed for `tier` given current `count`.
 * null cap == unlimited.
 */
export function withinQuota(effective, key, count) {
  const cap = quotaFor(effective, key);
  if (cap === null || cap === undefined) return true;
  return count < cap;
}

/**
 * Pure effective-tier resolver:
 *   effectiveTier = (status in {active,trialing} && !pausedAt) ? tier : "free"
 * Absent subscription → "free".
 */
export function effectiveTier(sub) {
  if (!sub) return "free";
  const tier = sub.tier || "free";
  const status = sub.status || "active";
  const paused = sub.pausedAt != null;
  return ACTIVE_STATUSES.has(status) && !paused ? tier : "free";
}
